import type { UTMParams } from "@src/context/UTMContext";
import type { DigitalFingerprint } from "@src/services/user-digital-info.service";

interface BrowserInfo {
  name: string;
  version: string;
}

/**
 * Parse the browser name and version from a User-Agent string.
 */
export const getBrowserInfo = (ua: string): BrowserInfo => {
  const edgeMatch = ua.match(/Edg\/([\d.]+)/i);
  if (edgeMatch) {
    return { name: "Edge", version: edgeMatch[1] };
  }
  const chromeMatch = ua.match(/Chrome\/([\d.]+)/i);
  if (chromeMatch && !/OPR\//i.test(ua)) {
    return { name: "Chrome", version: chromeMatch[1] };
  }
  const firefoxMatch = ua.match(/Firefox\/([\d.]+)/i);
  if (firefoxMatch) {
    return { name: "Firefox", version: firefoxMatch[1] };
  }
  const safariMatch = ua.match(/Version\/([\d.]+).*Safari/i);
  if (safariMatch) {
    return { name: "Safari", version: safariMatch[1] };
  }
  return { name: "Unknown", version: "Unknown" };
};

/**
 * Determine device type from the User-Agent string and optional UserAgentData.
 * The UA-based check is always evaluated so that device emulation in browser
 * DevTools (which changes the UA string but may leave userAgentData untouched)
 * is detected correctly.
 *
 * Modern iPads (iPadOS 13+) send a desktop-class "Macintosh" UA that is
 * indistinguishable from a real Mac by string alone.  We therefore also
 * check maxTouchPoints – iPads report > 0 while Macs report 0.
 */
export const getDeviceType = (
  ua: string,
  isMobileHint?: boolean,
  maxTouchPoints: number = 0,
): "desktop" | "mobile" | "tablet" => {
  const uaIsMobile = /Mobi|iPhone|iPod/i.test(ua);
  const isMobile = uaIsMobile || (isMobileHint ?? false);
  const isAndroid = /Android/i.test(ua);
  const isiPad = /iPad/i.test(ua);
  // iPadOS 13+ sends a Macintosh UA; detect it via touch support
  const isDesktopModeiPad = !isiPad && /Macintosh/i.test(ua) && maxTouchPoints > 0;
  const isTablet = !isMobile && (isAndroid || isiPad || isDesktopModeiPad);
  return isTablet ? "tablet" : isMobile ? "mobile" : "desktop";
};

/**
 * Determine the OS name based on device type and User-Agent.
 */
export const getOSName = (
  deviceType: "desktop" | "mobile" | "tablet",
  platform: string,
  ua: string,
): string => {
  if (deviceType === "desktop") {
    return platform;
  }
  if (/Android/i.test(ua)) {
    return "Android";
  }
  if (/iPhone|iPod/i.test(ua)) {
    return "iOS";
  }
  if (/iPad/i.test(ua) || /Macintosh/i.test(ua)) {
    return "iPadOS";
  }
  return "unknown";
};

/**
 * Attempt to retrieve the OS version via the User-Agent Client Hints API.
 * Returns null when the API is unavailable.
 */
export const getOSVersion = async (): Promise<string | null> => {
  const userAgentData = (navigator as any).userAgentData as
    | {
        getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
      }
    | undefined;

  if (userAgentData?.getHighEntropyValues) {
    const { platformVersion } = await userAgentData.getHighEntropyValues(["platformVersion"]);
    return platformVersion ?? "unknown";
  }
  return null;
};

/**
 * Build a complete DigitalFingerprint from the browser environment and
 * optional UTM parameters.
 */
export const buildDigitalFingerprint = async (utmTags?: UTMParams): Promise<DigitalFingerprint> => {
  const { userAgent, platform, language } = navigator;
  const userAgentData = (navigator as any).userAgentData as { mobile?: boolean } | undefined;

  const browserInfo = getBrowserInfo(userAgent);
  const osVersion = await getOSVersion();
  const deviceType = getDeviceType(userAgent, userAgentData?.mobile, navigator.maxTouchPoints ?? 0);
  const osName = getOSName(deviceType, platform, userAgent);

  return {
    browser_name: browserInfo.name,
    browser_version: browserInfo.version,
    os_name: osName,
    ...(osVersion && { os_version: osVersion }),
    device_type: deviceType,
    language,
    utm_source: utmTags?.utm_source || undefined,
    utm_medium: utmTags?.utm_medium || undefined,
    utm_campaign: utmTags?.utm_campaign || undefined,
    utm_content: utmTags?.utm_content || undefined,
    utm_theme: utmTags?.utm_theme || undefined,
  };
};
